import { c as _c } from "react/compiler-runtime";
import type { ItemQuantityPriceDto } from "@sticker-giant/sg-frontend-common";
import { useLayoutEffect, useState } from "react";
import { Select, SelectContent, SelectTrigger, SelectValue } from "@/components/ui/select";
import { trackQuantitySelected } from "@/lib/analytics/segment";
import { getQuantityOptionsForMaterial, isQuantityValid, isSoleQuantityOne } from "@/lib/product/quantities";
import { useProduct } from "@/queries/useProduct";
import { useCustomQuantityPricing, useQuantityPricing } from "@/queries/useQuantityPricing";
import { usePdpStore } from "@/stores/usePdpStore";
import CustomQuantity from "./CustomQuantity";
import OptionLabel from "./OptionLabel";
import { useProductBuilderContext } from "./ProductBuilderContext";
import QuantitySelectItems from "./QuantitySelectItems";
import { useProductMicrocopy } from "./useProductMicrocopy";
import ValidationError, { useFieldErrorId } from "./ValidationError";
function getSelectedValue(isCustom: boolean, selectedQuantity: number | undefined): string {
  if (selectedQuantity !== undefined) return selectedQuantity.toString();
  if (isCustom) return "custom";
  return "";
}
function buildPriceMap(pricingData?: {
  quantityPrices: ItemQuantityPriceDto[];
}) {
  return new Map(pricingData?.quantityPrices.map(qp => [qp.quantity, qp]) ?? []);
}
interface ConfirmedCustomParams {
  isCustom: boolean;
  selectedQuantity: number | undefined;
  presetValues: Set<number>;
  priceMap: Map<number, ItemQuantityPriceDto>;
  customPricingData?: {
    quantityPrices: ItemQuantityPriceDto[];
  };
}
function getConfirmedCustom({
  isCustom,
  selectedQuantity,
  presetValues,
  priceMap,
  customPricingData
}: ConfirmedCustomParams) {
  if (!isCustom || selectedQuantity === undefined || presetValues.has(selectedQuantity)) return undefined;
  const pricing = customPricingData?.quantityPrices[0] ?? priceMap.get(selectedQuantity);
  return {
    quantity: selectedQuantity,
    pricing
  };
}
export default function Quantity() {
  const $ = _c(39);
  const {
    product
  } = useProductBuilderContext();
  const {
    data: productAdmin
  } = useProduct(product.sku);
  const selectedMaterial = usePdpStore(_temp);
  const selectedQuantity = usePdpStore(_temp2);
  const setQuantity = usePdpStore(_temp3);
  const selectQuantity = usePdpStore(_temp4);
  const clearValidationError = usePdpStore(_temp5);
  const quantityErrorId = useFieldErrorId("quantity");
  const {
    data: pricingData
  } = useQuantityPricing(productAdmin);
  const {
    data: customPricingData
  } = useCustomQuantityPricing(productAdmin);
  const microcopyFields = useProductMicrocopy();
  const material = selectedMaterial ?? productAdmin?.defaultMaterial;
  const quantityOptions = productAdmin ? getQuantityOptionsForMaterial(productAdmin, material) : [];
  const presetValues = new Set(quantityOptions);
  const hasNonPresetQuantity = selectedQuantity !== undefined && !presetValues.has(selectedQuantity);
  const [customSelected, setCustomSelected] = useState(false);
  const isCustom = customSelected || hasNonPresetQuantity;
  const soleQuantityOne = isSoleQuantityOne(quantityOptions);
  useLayoutEffect(() => {
    if (soleQuantityOne) {
      if (selectedQuantity !== 1) {
        setQuantity(1);
      }
      return;
    }
    if (selectedQuantity !== undefined && productAdmin && !isQuantityValid(productAdmin, selectedQuantity, material)) {
      selectQuantity(undefined);
      setCustomSelected(false);
      clearValidationError("quantity");
    }
  }, [soleQuantityOne, selectedQuantity, productAdmin, material, setQuantity, selectQuantity, clearValidationError]);
  if (!productAdmin || quantityOptions.length === 0 || soleQuantityOne) {
    return null;
  }
  const quantityControlId = `quantity-${product.sku}`;
  const quantityLabelId = `${quantityControlId}-label`;
  const quantityHelpDialogId = `${quantityControlId}-help-dialog`;
  const quantityHelper = microcopyFields?.quantityHelper;
  const quantityLabel = microcopyFields?.quantityLabel ?? "Quantity";
  const priceMap = buildPriceMap(pricingData);
  const confirmedCustom = getConfirmedCustom({
    isCustom,
    selectedQuantity,
    presetValues,
    priceMap,
    customPricingData
  });
  const customQuantityLabel = confirmedCustom ? `Custom Quantity: ${confirmedCustom.quantity.toLocaleString()}` : "Custom Quantity";
  let t0;
  if ($[0] !== clearValidationError || $[1] !== selectQuantity || $[2] !== setCustomSelected) {
    t0 = function handleChange(value) {
      if (value === "custom") {
        setCustomSelected(true);
        trackQuantitySelected("custom");
        return;
      }
      setCustomSelected(false);
      const quantity = Number(value);
      selectQuantity(quantity);
      clearValidationError("quantity");
      trackQuantitySelected(quantity);
    };
    $[0] = clearValidationError;
    $[1] = selectQuantity;
    $[2] = setCustomSelected;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const handleChange = t0;
  let t1;
  if ($[4] !== quantityHelpDialogId || $[5] !== quantityHelper || $[6] !== quantityLabel || $[7] !== quantityLabelId) {
    t1 = <OptionLabel id={quantityLabelId} label={quantityLabel} helper={quantityHelper} helpDialogId={quantityHelpDialogId} />;
    $[4] = quantityHelpDialogId;
    $[5] = quantityHelper;
    $[6] = quantityLabel;
    $[7] = quantityLabelId;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  let t2;
  if ($[9] !== isCustom || $[10] !== selectedQuantity) {
    t2 = getSelectedValue(isCustom, selectedQuantity);
    $[9] = isCustom;
    $[10] = selectedQuantity;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  const t3 = `${quantityControlId}-content`;
  let t4;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <SelectValue placeholder="-- Select Quantity --" />;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== quantityErrorId || $[14] !== quantityLabelId || $[15] !== t3) {
    t5 = <SelectTrigger aria-labelledby={quantityLabelId} aria-describedby={quantityErrorId} aria-controls={t3} className="w-full">{t4}</SelectTrigger>;
    $[13] = quantityErrorId;
    $[14] = quantityLabelId;
    $[15] = t3;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const t6 = `${quantityControlId}-content`;
  let t7;
  if ($[17] !== confirmedCustom || $[18] !== customQuantityLabel || $[19] !== priceMap || $[20] !== productAdmin.allowCustomQuantity || $[21] !== quantityOptions) {
    t7 = <QuantitySelectItems quantityOptions={quantityOptions} priceMap={priceMap} allowCustomQuantity={productAdmin.allowCustomQuantity} customQuantityLabel={customQuantityLabel} confirmedCustom={confirmedCustom} />;
    $[17] = confirmedCustom;
    $[18] = customQuantityLabel;
    $[19] = priceMap;
    $[20] = productAdmin.allowCustomQuantity;
    $[21] = quantityOptions;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] !== t6 || $[24] !== t7) {
    t8 = <SelectContent id={t6} className="min-w-80">{t7}</SelectContent>;
    $[23] = t6;
    $[24] = t7;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] !== handleChange || $[27] !== t2 || $[28] !== t5 || $[29] !== t8) {
    t9 = <Select value={t2} onValueChange={handleChange}>{t5}{t8}</Select>;
    $[26] = handleChange;
    $[27] = t2;
    $[28] = t5;
    $[29] = t8;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  let t10;
  if ($[31] !== hasNonPresetQuantity || $[32] !== isCustom) {
    t10 = isCustom && <CustomQuantity prefill={hasNonPresetQuantity} />;
    $[31] = hasNonPresetQuantity;
    $[32] = isCustom;
    $[33] = t10;
  } else {
    t10 = $[33];
  }
  let t11;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <ValidationError field="quantity" />;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  let t12;
  if ($[35] !== t1 || $[36] !== t10 || $[37] !== t9) {
    t12 = <div className="flex flex-col gap-1" data-validation-field="quantity">{t1}{t9}{t10}{t11}</div>;
    $[35] = t1;
    $[36] = t10;
    $[37] = t9;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  return t12;
}
function _temp5(state_3) {
  return state_3.clearValidationError;
}
function _temp4(state_2) {
  return state_2.selectQuantity;
}
function _temp3(state_1) {
  return state_1.setQuantity;
}
function _temp2(state_0) {
  return state_0.quantity;
}
function _temp(state) {
  return state.material;
}